/* eslint-disable */
import * as THREE from 'three'
import OBJLoader from './OBJLoader/'
import TweenLite from 'gsap/TweenLite'
import { Power3, Back } from 'gsap/EasePack'
import CustomEase from './CustomEase'
import EventEmitter from './EventEmitter'

const tunnelVertexShader = require('./shaders/tunnel-2/vertex.glsl')
const tunnelFragmentShader = require('./shaders/tunnel-2/fragment.glsl')

export default class Scan2 extends EventEmitter {
    /**
     * Constructor
     */
    constructor(_options) {
        super()

        this.resources = _options.resources
        this.time = _options.time
        this.sizes = _options.sizes
        this.cursor = _options.cursor
        this.renderer = _options.renderer

        this.renderTarget = new THREE.WebGLRenderTarget(this.sizes.width, this.sizes.height, { minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, format: THREE.RGBAFormat })
        this.texture = this.renderTarget.texture
        this.name = 'scan2'
        this.hasLevels = true
        this.currentLevel = 1
        this.zoom = 1
        this.zoomOffset = 0
        this.headerWidth = 0
        this.headerWidthAndOffset = 0

        this.setScene()
        this.setModel()

        this.rayCaster = new THREE.Raycaster()
        this.cursorPosition = new THREE.Vector2()
    }

    /**
     * Go to level
     */
    goToLevel(index) {
        if (index === 1) {
            // Mask
            TweenLite.to(this.darkMaskPlane.material.uniforms.uMask, 0.5, { value: 1 })

            // Zoom
            TweenLite.to(this, 0.5, { zoomOffset: 0 })

            // Scan progress
            TweenLite.to(this.model.material.uniforms.uScanProgress, 1.0, { value: -0.02, delay: 0, ease: Power3.easeOut })
        } else if (index === 2) {
            // Mask
            TweenLite.to(this.darkMaskPlane.material.uniforms.uMask, 0.5, { value: 0 })

            // Zoom
            TweenLite.to(this, 0.5, { zoomOffset: 0.1 })

            // Scan progress
            TweenLite.to(this.model.material.uniforms.uScanProgress, 2, { value: 1, delay: 0, ease: Power3.easeInOut }) // CustomEase.create('custom', 'M0,0,C0.15,0,0.125,0.087,0.28,0.02,0.594,-0.118,0.334,1,1,1')
        }

        this.currentLevel = index
    }

    /**
     * Set scene
     */
    setScene() {
        // Scene
        this.scene = new THREE.Scene()

        // Camera
        this.camera = new THREE.PerspectiveCamera(40, this.sizes.width / this.sizes.height, 0.1, 1000)
        this.camera.originalRotation = new THREE.Vector3()
        this.camera.originalPosition = new THREE.Vector3()
        this.camera.rotation.order = 'YXZ'
        this.scene.add(this.camera)

        window.camera = this.camera

        this.mouse = {}
        this.mouse.x = 0.5
        this.mouse.y = 0.5

        this.cursor3d = new THREE.Vector3()

        // // Dummy
        // this.dummy = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.1, 0.1), new THREE.MeshBasicMaterial({ color: 0xff0000 }))
        // this.scene.add(this.dummy)
    }

    /**
     * Set model
     */
    setModel() {
        this.model = {}

        this.model.uniforms = {
            uTextureAlpha: { type: 't', value: this.resources.items.tunnelAlphaTexture },
            uTextureDefault: { type: 't', value: this.resources.items.tunnelDefaultTexture },
            uTextureScanned: { type: 't', value: this.resources.items.tunnelScannedTexture },
            uTime: { type: 'f', value: 0 },
            uCursorPosition: { type: 'v3', value: new THREE.Vector3() },
            uScanProgress: { type: 'f', value: -0.02 }
        }

        this.model.vertexShader = tunnelVertexShader
        this.model.fragmentShader = tunnelFragmentShader

        this.model.material = new THREE.ShaderMaterial({
            depthTest: true,
            depthWrite: false,
            transparent: true,
            uniforms: this.model.uniforms,
            vertexShader: this.model.vertexShader,
            fragmentShader: this.model.fragmentShader
        })

        // Meshes
        this.model.meshes = []
        this.model.container = new THREE.Object3D()
        this.scene.add(this.model.container)

        // Loader
        const objectLoader = new THREE.ObjectLoader()

        // Scene
        const meshOptions = {
            background: {
                renderOrder: 1
            },
            'ceiling-1': {
                renderOrder: 10
            },
            'ceiling-2': {
                renderOrder: 10
            },
            'ceiling-3': {
                renderOrder: 10
            },
            floor: {
                renderOrder: 3
            },
            men: {
                renderOrder: 10
            },
            roof: {
                renderOrder: 4
            },
            center: {
                renderOrder: 11
            }
        }
        const meshOptionsKeys = Object.keys(meshOptions)

        objectLoader.load('/static/3d/tunnel-test-1-data.json', data => {
            // Each object
            for (const object of data.children) {
                // Meshes
                if (object instanceof THREE.Mesh) {
                    if (meshOptionsKeys.indexOf(object.name) !== -1) {
                        const meshOption = meshOptions[object.name]

                        // Change material
                        object.material = this.model.material
                        object.renderOrder = meshOption.renderOrder

                        // Save
                        this.model.meshes.push(object)
                    }
                }

                // Camera
                if (object instanceof THREE.Camera) {
                    this.camera.fov = object.fov
                    this.camera.position.copy(object.position)
                    this.camera.rotation.copy(object.rotation)
                    this.camera.originalPosition.copy(object.position)
                    this.camera.originalRotation.copy(object.rotation)
                }
            }

            for (let i = 0; i < this.model.meshes.length; i++) {
                const mesh = this.model.meshes[i]
                this.model.container.add(mesh)
            }

            this.resize()
        })

        // White plane
        this.whitePlane = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial({ color: 0xf7f8fa, wireframe: false }))
        this.camera.add(this.whitePlane)

        // Dark mask
        const darkMaskMaterial = new THREE.ShaderMaterial({
            transparent: true,
            uniforms:
            {
                uMask: { type: 'f', value: 1 }
            },
            vertexShader: `
                uniform vec3 uCursorPosition;

                varying vec2 vUv;

                void main() {
                    vec4 newPosition = modelMatrix * vec4(position, 1.0);

                    vUv = uv;

                    gl_Position = projectionMatrix * viewMatrix * newPosition;
                }
            `,
            fragmentShader: `
                uniform float uMask;

                varying vec2 vUv;

                void main()
                {
                    float opacity = 0.7 - (1.0 - (vUv.y * 2.3 - 1.5)) * (1.0 - uMask);

                    gl_FragColor = vec4(vec3(0.0), opacity);
                }
            `
        })
        this.darkMaskPlane = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), darkMaskMaterial)
        this.darkMaskPlane.renderOrder = 20
        this.camera.add(this.darkMaskPlane)
    }

    /**
     * Resize
     */
    resize() {
        const sceneRatio = 3 / 2
        const viewportRatio = this.sizes.width / this.sizes.height

        if (sceneRatio < viewportRatio) {
            this.zoom = viewportRatio / sceneRatio
        } else {
            this.zoom = 1
        }

        // Camera
        this.camera.aspect = this.sizes.width / this.sizes.height

        // Render target
        this.renderTarget.setSize(this.sizes.width, this.sizes.height)
    }

    /**
     * Update
     */
    update() {
        // Cursor
        this.cursorPosition.x = this.cursor.fromCenter.x * 2
        this.cursorPosition.y = -this.cursor.fromCenter.y * 2
        this.rayCaster.setFromCamera(this.cursorPosition, this.camera)

        const intersects = this.rayCaster.intersectObjects(this.model.meshes, true)

        if (intersects.length > 0) {
            this.cursor3d.copy(intersects[0].point)
        }

        this.model.material.uniforms.uTime.value = this.time.elapsed

        this.model.material.uniforms.uCursorPosition.value.x += (this.cursor3d.x - this.model.material.uniforms.uCursorPosition.value.x) * 0.005 * this.time.delta
        this.model.material.uniforms.uCursorPosition.value.y += (this.cursor3d.y - this.model.material.uniforms.uCursorPosition.value.y) * 0.005 * this.time.delta
        this.model.material.uniforms.uCursorPosition.value.z += (this.cursor3d.z - this.model.material.uniforms.uCursorPosition.value.z) * 0.005 * this.time.delta

        // Camera
        this.camera.zoom = this.zoom + this.zoomOffset
        this.camera.updateProjectionMatrix()

        this.camera.position.copy(this.camera.originalPosition)
        this.camera.rotation.copy(this.camera.originalRotation)

        this.mouse.x += (this.cursor.fromCenter.x - this.mouse.x) * 0.005 * this.time.delta
        this.mouse.y += (this.cursor.fromCenter.y - this.mouse.y) * 0.005 * this.time.delta

        this.camera.position.z += 0.5
        this.camera.position.y += this.mouse.y * 0.3
        this.camera.position.x -= this.mouse.x * 0.3

        // Fix a minimum headerWidth
        // -> 1/3 of the width
        this.headerWidthAndOffset = this.headerWidth
        if (this.headerWidth < this.sizes.width / 3) {
            this.headerWidthAndOffset = this.sizes.width / 3
        }

        // White plane
        this.whitePlane.position.x = 0
        this.whitePlane.position.z = -1

        let fullScaleX = Math.tan(Math.PI * this.camera.fov / 2 / 180) * 2 / this.zoom * this.camera.aspect
        let fullScaleY = Math.tan(Math.PI * this.camera.fov / 2 / 180) * 2 / this.zoom
        let reducedScaleX = fullScaleX * (1 - this.headerWidthAndOffset / this.sizes.width)
        let scaleDiff = fullScaleX - reducedScaleX
        this.whitePlane.scale.x = fullScaleX
        this.whitePlane.scale.y = fullScaleY
        this.whitePlane.position.x -= scaleDiff

        // Dark mask
        this.darkMaskPlane.position.x = 0
        this.darkMaskPlane.position.z = -1

        fullScaleX = Math.tan(Math.PI * this.camera.fov / 2 / 180) * 2 / this.zoom * this.camera.aspect
        fullScaleY = Math.tan(Math.PI * this.camera.fov / 2 / 180) * 2 / this.zoom
        reducedScaleX = fullScaleX * (this.headerWidthAndOffset / this.sizes.width)
        scaleDiff = fullScaleX - reducedScaleX
        this.darkMaskPlane.scale.x = fullScaleX
        this.darkMaskPlane.scale.y = fullScaleY
        this.darkMaskPlane.position.x += scaleDiff

        // Render
        this.render()
    }

    /**
     * Render
     */
    render() {
        this.renderer.render(this.scene, this.camera, this.renderTarget)
    }

    /**
     * Dispose
     */
    dispose() {
        for (let i = 0; i < this.model.meshes.length; i++) {
            const mesh = this.model.meshes[i]

            mesh.geometry.dispose()
        }

        this.texture.dispose()
    }
}



// WEBPACK FOOTER //
// ./src/scripts/libs/experience/Scan2.js